<template>
  <v-card class="ModalWebhookTester" max-width="800px" dark>
    <v-card-title class="headline justify-center">
      Test Webhook
    </v-card-title>

    <v-card-text class="mt-6 text">
      <div class="mb-4">
        <h6>Test Type</h6>
        <v-radio-group v-model="testType" row>
          <v-radio label="Sample Data" value="sample_data"></v-radio>
          <v-radio label="Form Submission" value="form_submission" :disabled="!webhook.form_id"></v-radio>
        </v-radio-group>
      </div>

      <div v-if="testType === 'form_submission' && webhook.form_id" class="mb-4">
        <h6>Form Submission Data</h6>
        <v-select
          v-model="selectedFormDatum"
          :items="formData"
          item-text="label"
          item-value="value"
          return-object
          dense
          filled
          single-line
          placeholder="Select form submission to test with"
          clearable
        ></v-select>
      </div>

      <div class="mb-4">
        <h6>Sample Payload Preview</h6>
        <v-card outlined class="pa-4">
          <pre class="payload-preview">{{ formattedPayload }}</pre>
        </v-card>
      </div>

      <div v-if="testResult" class="mb-4">
        <h6>Test Result</h6>
        <v-card outlined class="pa-4">
          <div class="d-flex align-center mb-2">
            <v-chip :color="testResult.status >= 200 && testResult.status < 300 ? 'success' : 'error'" small>
              {{ testResult.status }} {{ testResult.statusText }}
            </v-chip>
          </div>
          <div v-if="testResult.response_body">
            <h6>Response Body:</h6>
            <pre class="response-body">{{ testResult.response_body }}</pre>
          </div>
        </v-card>
      </div>
    </v-card-text>

    <v-card-actions class="mx-auto pa-6 justify-center">
      <v-btn @click="runTest" color="y1" :loading="isTesting" :disabled="isTesting">
        {{ isTesting ? 'Testing...' : 'Run Test' }}
      </v-btn>
      <v-btn @click="close" color="n5" class="ml-7">Close</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapState } from 'vuex'
import { Form as FormResource } from '@/resources/forge'

export default {
  name: 'ModalWebhookTester',
  props: {
    webhook: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      testType: 'sample_data',
      selectedFormDatum: null,
      formData: [],
      testResult: null,
      isTesting: false,
      samplePayload: null
    }
  },
  computed: {
    ...mapState({
      activeSite: (state) => state.sites.active,
    }),
    formattedPayload() {
      if (!this.samplePayload) return 'Loading...'
      return JSON.stringify(this.samplePayload, null, 2)
    }
  },
  async created() {
    await this.loadFormData()
    this.generateSamplePayload()
  },
  methods: {
    async loadFormData() {
      if (!this.webhook.form_id) return
      
      try {
        const response = await FormResource.show({ id: this.webhook.form_id })
        const form = response.body.form
        
        if (form.form_data && form.form_data.length > 0) {
          this.formData = form.form_data.slice(0, 10).map((datum, index) => ({
            label: `Submission ${index + 1} - ${new Date(datum.timestamp).toLocaleString()}`,
            value: datum.id,
            data: datum.data
          }))
        }
      } catch (error) {
        console.error('Error loading form data:', error)
      }
    },
    generateSamplePayload() {
      if (this.webhook.event === 'form_submission') {
        this.samplePayload = {
          form_id: this.webhook.form_id || '123',
          form_name: this.webhook.form_name || 'contact_form',
          form_human_name: this.webhook.form_human_name || 'Contact Us',
          form_scope: this.webhook.form_scope || 'contact',
          form_datum_id: '789',
          form_submitted_at: new Date().toISOString(),
          form_fields: ['name', 'email', 'message'],
          form_field_count: 3,
          name: 'John Doe',
          email: 'john@example.com',
          message: 'Test message'
        }
      } else if (this.webhook.event === 'deploy_success') {
        this.samplePayload = {
          site_id: this.activeSite.id,
          site_url: this.activeSite.url,
          deployment_status: 'success',
          deployed_at: new Date().toISOString(),
          version: '1.0.0'
        }
      } else if (this.webhook.event === 'deploy_failure') {
        this.samplePayload = {
          site_id: this.activeSite.id,
          site_url: this.activeSite.url,
          deployment_status: 'failed',
          failed_at: new Date().toISOString(),
          error_message: 'Build failed due to compilation errors'
        }
      } else {
        this.samplePayload = {
          event: this.webhook.event,
          timestamp: new Date().toISOString(),
          site_id: this.activeSite.id
        }
      }
    },
    async runTest() {
      this.isTesting = true
      this.testResult = null
      
      try {
        const testParams = {
          test_type: this.testType
        }
        
        if (this.testType === 'form_submission' && this.selectedFormDatum) {
          testParams.form_datum_id = this.selectedFormDatum.value
        }
        
        // Call the actual API endpoint
        const response = await this.$http.post(`/internal_api/webhook_triggers/${this.webhook.id}/test`, testParams)
        
        this.testResult = {
          status: response.status,
          statusText: response.statusText || 'OK',
          response_body: response.body ? JSON.stringify(response.body, null, 2) : 'No response body'
        }
        
      } catch (error) {
        console.error('Error testing webhook:', error)
        this.testResult = {
          status: error.status || 500,
          statusText: error.statusText || 'Internal Server Error',
          response_body: error.body?.message || error.message || 'An error occurred while testing the webhook'
        }
      } finally {
        this.isTesting = false
      }
    },
    close() {
      this.$emit('close')
    }
  }
}
</script>

<style lang="scss" scoped>
$style: ModalWebhookTester;
.#{$style} {
  &.theme--dark.v-card {
    @extend %modal-body-styles;
    min-width: 800px;
    padding: 24px 24px 0;
  }
}
.text {
  font-size: 13px;
}
.payload-preview, .response-body {
  background: rgba(255, 255, 255, 0.1);
  padding: 12px;
  border-radius: 4px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  line-height: 1.4;
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
}
</style> 